import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
import { customerInfoProviderToken } from "@/tokens";
import { TRANSLATION_NAMESPACES } from "@apps/rewards/constants/constants";
import RewardsStorefrontValuesService from "@apps/rewards/modules/StorefrontValues/service/RewardsStorefrontValuesService";
import RewardsDiResolverService from "@apps/rewards/services/RewardsDiResolverService/RewardsDIResolverService";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { CommonModule } from "@modules/common_module/CommonModule";
import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
import { I18nModule } from "@modules/i18n/I18nModule";
import { injectable } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";
import type { WidgetInterface } from "@interfaces/WidgetInterface";

import RewardsPopUpWidgetComponent from "./RewardsPopUpWidget.svelte";
import type { RewardsPopUpWidgetConfigInterface } from "./RewardsPopUpWidgetConfig";
import RewardsPopUpWidgetLauncherManager from "./utils/RewardsPopUpWidgetLauncherManager/RewardsPopUpWidgetLauncherManager";

@injectable()
class RewardsPopUpWidget implements WidgetInterface {
    constructor(
        private readonly config: RewardsPopUpWidgetConfigInterface,
        private readonly logger: LoggerInterface
    ) {
        this.logger.debug("RewardsPopUpWidget is initialized", config);
    }

    async init(): Promise<this> {
        const widgetName = REWARDS_WIDGET_NAMES.REWARDS_POP_UP_WIDGET;
        const widgetNamespace = TRANSLATION_NAMESPACES.criticalPopUpWidget;

        const diResolverService = new RewardsDiResolverService();
        const {
            i18nModule,
            storefrontValuesService,
            launcherManager,
            dynamicImportModule,
            commonModule,
            customizationManager,
            customerInfoProvider,
        } = diResolverService.resolveAll({
            i18nModule: I18nModule,
            storefrontValuesService: RewardsStorefrontValuesService,
            launcherManager: RewardsPopUpWidgetLauncherManager,
            dynamicImportModule: DynamicImportModule,
            commonModule: CommonModule,
            customizationManager: CustomizationManager,
            customerInfoProvider: customerInfoProviderToken,
        });

        const { i18n, i18nCriticalLoaderFactory } = i18nModule.exports;
        i18n.registerLoader(
            widgetNamespace,
            i18nCriticalLoaderFactory.getLoader(widgetNamespace)
        );

        const element = this.config.target;
        const placeholder = document.createElement("div");
        placeholder.classList.add("gw-rd-popup-widget-placeholder");
        placeholder.style.display = "block";
        element.appendChild(placeholder);

        const shadowRoot =
            placeholder.shadowRoot ||
            placeholder.attachShadow({ mode: "open" });

        const customizationManagerConfig = {
            widgetName,
            placeholder,
            shadowRoot,
        };
        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );

        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        new RewardsPopUpWidgetComponent({
            target: shadowRoot,
            props: {
                storefrontValuesService,
                dynamicImportManager:
                    dynamicImportModule.exports.dynamicImportManager,
                launcherManager,
                i18n,
                hashParamsProvider: commonModule.exports.hashParamsProvider,
                logger: this.logger,
                widgetName,
                widgetNamespace,
                customerInfoProvider,
                afterWidgetInject: () => {
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    );
                },
            },
        });

        return this;
    }
}

export default RewardsPopUpWidget;
